import { useId } from 'react';
import type { ReactNode, SVGProps } from 'react';

/**
 * ============================================================================
 * ICON SYSTEM
 * ============================================================================
 * One coherent outline-icon family for the whole app — medium stroke
 * weight (1.75), round caps/joins, 24x24 viewBox, currentColor stroke so
 * every icon inherits its surrounding text colour (nav active/inactive,
 * copper accents, etc. all work automatically with no per-icon colour
 * logic). Pure inline SVG — no icon library, no new dependency.
 *
 * Replaces the previous emoji-based icons used in BottomNav, event types
 * (eventDisplay.ts's EVENT_TYPE_ICONS), Records (formerly Report/Backup),
 * and the expand/collapse chevrons used on Home/Schedule.
 */

interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number;
}

function Icon({ size = 24, children, ...rest }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 11.5 12 4l8 7.5" />
      <path d="M6 10v9a1 1 0 0 0 1 1h3v-6h4v6h3a1 1 0 0 0 1-1v-9" />
    </Icon>
  );
}

export function TimelineIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <line x1="12" y1="3.5" x2="12" y2="20.5" />
      <circle cx="12" cy="7" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="12" cy="17" r="1.6" />
    </Icon>
  );
}

export function ScheduleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="8.5" y1="3.5" x2="8.5" y2="7" />
      <line x1="15.5" y1="3.5" x2="15.5" y2="7" />
      <line x1="8" y1="14" x2="8" y2="14" />
      <line x1="12" y1="14" x2="12" y2="14" />
      <line x1="16" y1="14" x2="16" y2="14" />
    </Icon>
  );
}

/** Nav-level Records icon: an open book/ledger — distinct from the single-page Reports icon, since Records combines Reports + Backup & Restore. */
export function RecordsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 6.5c-1.6-1-3.6-1.5-5.5-1.5a1 1 0 0 0-1 1v11.5a1 1 0 0 0 1 1c1.9 0 3.9.5 5.5 1.5" />
      <path d="M12 6.5c1.6-1 3.6-1.5 5.5-1.5a1 1 0 0 1 1 1v11.5a1 1 0 0 1-1 1c-1.9 0-3.9.5-5.5 1.5" />
      <line x1="12" y1="6.5" x2="12" y2="19.5" />
    </Icon>
  );
}

/** A single page/document — the Reports sub-section within Records. */
export function ReportsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 3.5h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-16a1 1 0 0 1 1-1Z" />
      <path d="M14 3.5v4h4" />
      <line x1="8.5" y1="12" x2="15.5" y2="12" />
      <line x1="8.5" y1="15.5" x2="13.5" y2="15.5" />
    </Icon>
  );
}

/** A cloud with an upward arrow — Backup & Restore (local and Drive). */
export function BackupRestoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M7 17.5a4 4 0 0 1-.7-7.94 5 5 0 0 1 9.73-1.6A4.5 4.5 0 0 1 17 17.5Z" />
      <line x1="12" y1="11" x2="12" y2="17" />
      <polyline points="9.5 13.2 12 10.7 14.5 13.2" />
    </Icon>
  );
}

/** Small chevron, rotates via CSS transform where used — replaces the old ▾ / ▸ glyphs for expand/collapse disclosure. */
export function ChevronIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <polyline points="8 10 12 14 16 10" />
    </Icon>
  );
}

// ---------------------------------------------------------------------------
// Event-type icons (used by eventDisplay.ts's EVENT_TYPE_ICONS)
// ---------------------------------------------------------------------------

export function DisbursementIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <line x1="12" y1="4" x2="12" y2="16" />
      <polyline points="7 11 12 16 17 11" />
      <line x1="5" y1="20" x2="19" y2="20" />
    </Icon>
  );
}

/** A genuine percentage sign — specific to "interest rate", not a generic trend/chart glyph. */
export function RateChangeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <line x1="6" y1="18" x2="18" y2="6" />
      <circle cx="7.5" cy="7.5" r="2.25" />
      <circle cx="16.5" cy="16.5" r="2.25" />
    </Icon>
  );
}

/** An arrow paying down a stack — distinct from Disbursement's single-baseline arrival, since this is principal being reduced ahead of schedule, not funds arriving. */
export function PrepaymentIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <line x1="12" y1="3.5" x2="12" y2="13.5" />
      <polyline points="7.5 9.5 12 14 16.5 9.5" />
      <line x1="5.5" y1="17.5" x2="18.5" y2="17.5" />
      <line x1="6.5" y1="20.5" x2="17.5" y2="20.5" />
    </Icon>
  );
}

export function BalanceCorrectionIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.25" />
      <polyline points="8.5 12.25 10.8 14.5 15.5 9.5" />
    </Icon>
  );
}

export function ChargeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3.5h12a1 1 0 0 1 1 1V20l-2.5-1.6L14 20l-2-1.6L10 20l-2.5-1.6L5 20V4.5a1 1 0 0 1 1-1Z" />
      <line x1="8.5" y1="8" x2="15.5" y2="8" />
      <line x1="8.5" y1="11.5" x2="13.5" y2="11.5" />
    </Icon>
  );
}

export function EmiDateChangeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2" />
      <line x1="4" y1="10" x2="20" y2="10" />
      <line x1="8.5" y1="3.5" x2="8.5" y2="7" />
      <line x1="15.5" y1="3.5" x2="15.5" y2="7" />
      <circle cx="16" cy="15.5" r="2.6" />
      <line x1="16" y1="14.3" x2="16" y2="15.5" />
      <line x1="16" y1="15.5" x2="16.9" y2="16" />
    </Icon>
  );
}

// ---------------------------------------------------------------------------
// Brand mark (Concept 26 — "The Archive Key-Cube")
// ---------------------------------------------------------------------------

interface BrandMarkProps {
  size?: number;
  /** Include the dark aubergine background square (as used by the favicon/app icon). Off by default for in-app use, where the mark sits directly on the app's own background. */
  withBackground?: boolean;
  className?: string;
}

/**
 * The approved Debdeep's Ledger brand mark — the exact geometry used by
 * public/favicon.svg (layered three-course block stack with a "D"-shaped
 * keyhole cut as true negative space), transcribed here as a React
 * component so it can appear once, deliberately, in the app's own
 * shared chrome (see App.tsx) rather than only in the browser tab.
 * This is NOT a redesign — the path/rect coordinates are copied
 * unchanged from the favicon.
 */
export function BrandMark({ size = 28, withBackground = false, className }: BrandMarkProps) {
  // A unique id per rendered instance — without this, two BrandMarks on
  // the same page would collide on the same SVG mask id and one could
  // render incorrectly (a real SVG footgun, not a style nitpick).
  const maskId = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      className={className}
      role="img"
      aria-label="Debdeep's Ledger"
    >
      {withBackground && <rect width="32" height="32" rx="7" fill="#1A1220" />}
      <mask id={maskId}>
        <rect width="32" height="32" fill="white" />
        <path d="M13 9 L13 23 Q21 23 21 16 Q21 9 13 9 Z" fill="black" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <rect x="6" y="7" width="20" height="5" rx="1.1" fill="#C7834A" />
        <rect x="6" y="13.2" width="20" height="5" rx="1.1" fill="#C7834A" />
        <rect x="6" y="19.4" width="20" height="5" rx="1.1" fill="#C7834A" />
      </g>
    </svg>
  );
}
